import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dimmer } from 'semantic-ui-react';
import {
  ATMButton,
  ATMLoader,
  ATMTab,
  ATMModal,
  IORGDataTableQueryState,
} from 'shared-it-appmod-ui';
import { useRequestContext } from 'src/contexts/request.context';
import { actionTypes } from 'src/ducks/request.duck';
import {
  getRequestStatus,
  isRequestAccessible,
} from 'src/selectors/request.selector';
import history from 'src/history';
import { getUrl, getUrlParams } from 'src/libraries/common.library';
// import { IRequest } from 'src/models/request.model';
import Lang from 'src/libraries/language';
import ChangeLogs from 'src/components/pages/change-logs/change-logs.component';
import Access, {
  AccessType,
} from 'src/components/atoms/access/access.component';
import FlightRequestDetailView from './flight-request-detail.view';
import FlightRequestHeader from '../flight-request-header/flight-request-header.component';
import FlightRequestDropdown from '../flight-request-dropdown/flight-request-dropdown.component';
import styles from './flight-request-detail.module.scss';

export type IProps = {
  requestId?: number;
  type?: string;
};

const FlightRequestDetail: React.FC<IProps> = ({ requestId, type }) => {
  const { state, actions } = useRequestContext();
  const { state: logState, actions: logActions } = useRequestContext();

  const status = getRequestStatus(state, actionTypes.REQUEST_DATA_FETCH);

  const handleClose = useCallback(() => {
    const params = getUrlParams(history.location.search);

    if (params.from) {
      history.push(`/${params.from}`);
    } else {
      history.push(
        getUrl(
          {
            requestId: undefined,
            category: undefined,
          },
          '/flight'
        )
      );
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const result = await actions.dataGET(requestId, type);
      if (result.error) {
        handleClose();
      }
    };

    if (requestId && type) {
      fetchData();
    }
  }, [requestId, type, actions, handleClose]);

  useEffect(() => {
    if (
      state.data &&
      requestId &&
      state.data?.request?.requestId !== requestId
    ) {
      actions.setRequestData();
    }
  }, [requestId, state.data, actions]);

  const handleFetch = useCallback(
    (params: IORGDataTableQueryState) => {
      logActions.changeLogGET(requestId ?? 0, params);
    },
    [requestId, logActions]
  );

  let content: React.ReactNode = null;

  if (state.data && requestId && type) {
    const panes = [
      {
        menuItem: Lang.LBL_DETAILS,
        render: () => (
          <ATMTab.Pane>
            <FlightRequestDetailView
              data={state.data}
              requestId={requestId}
              type={type}
            />
          </ATMTab.Pane>
        ),
      },
      {
        menuItem: Lang.LBL_CHANGE_LOG,
        render: () => (
          <ATMTab.Pane>
            <ChangeLogs
              data={logState.changeLog}
              total={logState.logTotal}
              loading={
                getRequestStatus(logState, actionTypes.REQUEST_CHANGE_LOG_FETCH)
                  .fetching
              }
              handleFetch={handleFetch}
            />
          </ATMTab.Pane>
        ),
      },
    ];

    content = (
      <>
        <ATMModal.Header>
          <FlightRequestHeader data={state.data} requestId={requestId}>
            <>
              <Access
                type={AccessType.FLIGHT_REQUEST_UPDATE}
                when={isRequestAccessible(
                  {
                    requestId,
                    requestStatus: state.data?.request?.requestStatus,
                    requestorNo: state.data?.request?.requestorNo,
                  },
                  AccessType.FLIGHT_REQUEST_UPDATE
                )}
              >
                <ATMButton
                  secondary
                  icon="edit"
                  as={Link}
                  to={`/flight/${type}/${requestId}/edit`}
                  content="Edit"
                />
              </Access>

              <FlightRequestDropdown
                isList={false}
                requestId={state.data?.request?.requestId}
                requestStatus={state.data?.request?.requestStatus}
                requestorNo={state.data?.request?.requestorNo}
                type={type}
              />
            </>
          </FlightRequestHeader>
        </ATMModal.Header>
        <ATMModal.Content className={styles.content} scrolling>
          <ATMTab menu={{ secondary: true, pointing: true }} panes={panes} />
        </ATMModal.Content>
        <ATMModal.Actions>
          <ATMButton secondary onClick={handleClose}>
            {Lang.LBL_CLOSE}
          </ATMButton>
        </ATMModal.Actions>
      </>
    );
  }

  if (
    !!(requestId && type) &&
    (!state.data ||
      (state.data && state.data?.request?.requestId !== requestId)) &&
    status.fetching
  ) {
    return (
      <Dimmer.Inner className={styles.backdrop} active onClick={handleClose}>
        <ATMLoader inline="centered">{Lang.LBL_LOADING}</ATMLoader>
      </Dimmer.Inner>
    );
  }

  return (
    <ATMModal
      open={!!(requestId && type)}
      size="fullscreen"
      closeOnDimmerClick={false}
      onClose={handleClose}
    >
      {content}
    </ATMModal>
  );
};

export default FlightRequestDetail;
